import { makeAutoObservable, observable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { registerBillingCapabilities } from "./billingGate";

import { sessionMark } from "stores/sessionGuard";
/** One optional feature and the env keys it still needs. */
export interface CapabilityReport {
  configured: boolean;
  missing: string[];
}

export interface BillingCapabilities {
  enabled: boolean;
  missing: string[];
  email: CapabilityReport;
  contractParse: CapabilityReport;
  contractFiles: CapabilityReport;
  /** Render an invoice as a PDF (GET /invoices/:id/pdf). */
  invoicePdf: CapabilityReport;
  invoicePrefix: string;
  companyName: string;
}

/** Nothing is available until the API says otherwise. Fail closed. */
const CLOSED: BillingCapabilities = {
  enabled: false,
  missing: [],
  email: { configured: false, missing: [] },
  contractParse: { configured: false, missing: [] },
  contractFiles: { configured: false, missing: [] },
  invoicePdf: { configured: false, missing: [] },
  invoicePrefix: "INV",
  companyName: "",
};

/**
 * Whether this instance has billing configured, and which parts of it work.
 *
 * The API owns the decision (`billing-capabilities`); this only caches the
 * answer so route guards and headers can read it synchronously. It is loaded
 * once at boot and on demand — a config change needs a redeploy anyway, so
 * polling would buy nothing.
 *
 * Fails CLOSED: until the report arrives, and on any error, billing is treated
 * as unavailable. A missing capability endpoint is indistinguishable from a
 * deliberately unconfigured instance, and hiding the module is the safe answer
 * to both.
 */
export class BillingCapabilitiesStore {
  rootStore: RootStore;

  capabilities: BillingCapabilities = CLOSED;
  loaded = false;
  loading = false;

  /**
   * The load in progress, shared by every caller that arrives while it runs —
   * the route guard, the invoice actions and Client 360 can all ask at boot.
   * Tagged with the sign-in it began under: a load that straddles a sign-out
   * is not joined by the next person's callers.
   */
  private inFlight: { session: number; promise: Promise<BillingCapabilities> } | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable<BillingCapabilitiesStore, "inFlight">(this, {
      rootStore: false,
      inFlight: false,
      // Replaced whole, never edited in place — and held by reference, so
      // `reset()` can tell the latched `CLOSED` default from a real answer.
      capabilities: observable.ref,
    });
    // Hand the live instance to the route gate: billing routes are built at
    // import time and cannot reach the RootStore any other way, and their
    // `hidden` getter has to read THIS store to stay reactive.
    registerBillingCapabilities(this);
  }

  get enabled(): boolean {
    return this.capabilities.enabled;
  }

  get canEmail(): boolean {
    return this.capabilities.email.configured;
  }

  get canParseContracts(): boolean {
    return this.capabilities.contractParse.configured;
  }

  get canStoreContracts(): boolean {
    return this.capabilities.contractFiles.configured;
  }

  /**
   * Whether this instance can render an invoice PDF.
   *
   * Separate from `enabled` on purpose: the module only needs a company NAME,
   * but a document that leaves the building also needs an address to remit to.
   * An instance with one and not the other runs billing perfectly well and
   * simply cannot produce a payable document — so the action is offered
   * DISABLED with the reason, not silently missing and not silently broken.
   */
  get canDownloadInvoicePdf(): boolean {
    return this.enabled && this.capabilities.invoicePdf.configured;
  }

  /** Why the PDF action is unavailable, naming the variables ops must set. */
  get invoicePdfDisabledReason(): string {
    if (!this.enabled) return this.disabledReason;
    const missing = this.capabilities.invoicePdf.missing;
    if (!missing.length) return "Invoice PDFs are unavailable on this instance.";
    return `Invoice PDFs need the issuing company on the document. Missing: ${missing.join(", ")}.`;
  }

  /** Human-readable reason the module is hidden, for the ops-facing message. */
  get disabledReason(): string {
    const missing = this.capabilities.missing;
    if (!missing.length) return "Billing is not enabled on this instance.";
    return `Billing is not configured on this instance. Missing: ${missing.join(", ")}.`;
  }

  /**
   * Sign-out. The answer itself is NOT cleared: it is this instance's billing
   * configuration, the same for whoever signs in next, and the nav's `hidden`
   * getter reads it — clearing it would hide Billing from the next person until
   * something happened to ask again. What belongs to the session that ended is
   * the load still in progress: it is dropped (its answer is discarded by the
   * session mark), and the next caller starts a fresh one.
   *
   * ONLY AN ANSWER IS KEPT, NOT A FAILURE. A failed load latches the closed
   * default (`CLOSED`, by identity — the API's own "not configured" is a
   * different object) so this session does not keep re-asking; but that is the
   * session that ended failing to read the configuration — a 401 as its token
   * expired, a network blip — not the configuration. Kept, it hid Billing from
   * the next person until a page reload, because `load()` short-circuits on
   * `loaded`. So a failure is forgotten here and the next caller asks again.
   */
  reset() {
    this.inFlight = null;
    this.loading = false;
    if (this.capabilities === CLOSED) this.loaded = false;
  }

  /**
   * The capability report. Callers that arrive while a load is in progress
   * await THAT load — they used to get the closed default back at once, so the
   * route guard turned a slow first answer into "Billing is not enabled" and a
   * redirect Home.
   */
  load(force = false): Promise<BillingCapabilities> {
    if (this.loaded && !force) return Promise.resolve(this.capabilities);
    const session = this.rootStore?.sessionEpoch ?? 0;
    if (this.inFlight && this.inFlight.session === session) return this.inFlight.promise;

    this.loading = true;
    const promise = this.fetchCapabilities().finally(() => {
      if (this.inFlight?.promise === promise) {
        this.inFlight = null;
        runInAction(() => {
          this.loading = false;
        });
      }
    });
    this.inFlight = { session, promise };
    return promise;
  }

  private async fetchCapabilities(): Promise<BillingCapabilities> {
    // A load that straddles a sign-out is rejected by the session guard;
    // latching that as "closed + loaded" would hide Billing from the next
    // person for the store's lifetime, so it latches nothing.
    const ended = sessionMark(this.rootStore);
    try {
      const app = this.rootStore.app as
        | { service: (p: string) => { find: (params?: object) => Promise<unknown> } }
        | undefined;
      const result = (await app?.service("billing-capabilities").find()) as
        | BillingCapabilities
        | undefined;

      if (ended()) return this.capabilities;
      runInAction(() => {
        this.capabilities = result && typeof result === "object" ? result : CLOSED;
        this.loaded = true;
      });
    } catch {
      if (ended()) return this.capabilities;
      // Fail closed — see the class docblock.
      runInAction(() => {
        this.capabilities = CLOSED;
        this.loaded = true;
      });
    }
    return this.capabilities;
  }
}

export default BillingCapabilitiesStore;
